"use client";

import React, { useEffect, useState } from "react";
import { Box, Dialog, Fade, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Fullscreen splash shown on every visit — appears immediately on entry,
// reveals the close button after a delay, then auto-closes after the
// configured timeout. All settings are driven by the CMS splash-manager
// page and passed in as props from the server; the image is served from OSS.
const DEFAULT_CLOSE_DELAY_MS = 3000;
const DEFAULT_AUTO_CLOSE_MS = 8000;

export default function SplashModal({
  enabled = true,
  imageUrl = "/splash.jpg",
  closeButtonDelayMs = DEFAULT_CLOSE_DELAY_MS,
  autoCloseMs = DEFAULT_AUTO_CLOSE_MS,
}: {
  enabled?: boolean;
  imageUrl?: string;
  closeButtonDelayMs?: number;
  autoCloseMs?: number;
}) {
  const [open, setOpen] = useState(true);
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const revealTimer = setTimeout(() => setCanClose(true), closeButtonDelayMs);
    const autoCloseTimer = setTimeout(() => setOpen(false), autoCloseMs);
    return () => {
      clearTimeout(revealTimer);
      clearTimeout(autoCloseTimer);
    };
  }, [enabled, closeButtonDelayMs, autoCloseMs]);

  if (!enabled) return null;

  return (
    <Dialog
      open={open}
      // Only allow dismissing once the close button is revealed.
      onClose={() => canClose && setOpen(false)}
      fullScreen
      slotProps={{
        paper: {
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        },
      }}
    >
      <Fade in={canClose} unmountOnExit>
        <IconButton
          aria-label="close"
          onClick={() => setOpen(false)}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "#fff",
            bgcolor: "rgba(0, 0, 0, 0.4)",
            zIndex: 1,
            "&:hover": { bgcolor: "rgba(0, 0, 0, 0.6)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Fade>

      <Box
        sx={{
          position: "relative",
          width: "90%",
          height: "100%",
          mx: "auto",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box
          component="img"
          src={imageUrl || "/splash.jpg"}
          alt="Splash"
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
        />
      </Box>
    </Dialog>
  );
}
